/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import type { QueryClient } from '@tanstack/react-query';
import {
  FILTER_STATE_KEY,
  MAX_VALUE,
  MIN_VALUE,
  SORT_BY,
} from '../configs/constants';
import type { HotelFilters, SafetyNetSelectedHotel } from '../types';

const getDefaultHotelFilters = (): Omit<
  HotelFilters,
  'selectedHotelId' | 'selectedHotelName'
> => ({
  priceRange: [MIN_VALUE, MAX_VALUE],
  selectedPriceRanges: [],
  sortBy: SORT_BY.DEFAULT,
  selectedPromos: [],
  selectedRateAmenities: [],
  guestRating: '',
  starRating: [],
  selectedAmenities: [],
  selectedPropertyTypes: [],
  selectedPaymentPolicies: [],
  selectedLandmark: undefined,
});

const SELECTED_HOTEL_QUERY_KEYS = new Set([
  'aaId',
  'user_selected_hotel_id',
  'user_selected_id',
]);

const getQueryString = (url: string) => {
  const queryStartIndex = url.indexOf('?');

  if (queryStartIndex === -1) {
    return '';
  }

  const hashStartIndex = url.indexOf('#', queryStartIndex);

  return hashStartIndex === -1
    ? url.slice(queryStartIndex + 1)
    : url.slice(queryStartIndex + 1, hashStartIndex);
};

const safeDecodeURIComponent = (value: string) => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

export const getSelectedHotelIdFromUrl = (url: string) => {
  const queryString = getQueryString(url);

  if (!queryString) {
    return '';
  }

  const selectedHotelPair = queryString
    .split('&')
    .map((pair) => {
      const [key, ...valueParts] = pair.split('=');
      const value = valueParts.join('=');

      return {
        key: safeDecodeURIComponent(key),
        value: safeDecodeURIComponent(value.replace(/\+/g, ' ')).trim(),
      };
    })
    .find(
      ({ key, value }) =>
        SELECTED_HOTEL_QUERY_KEYS.has(key) && value.length > 0,
    );

  return selectedHotelPair?.value ?? '';
};

export const removeEmptySelectedHotelQueryParams = (url: string) => {
  const queryStartIndex = url.indexOf('?');

  if (queryStartIndex === -1) {
    return url;
  }

  const routePath = url.slice(0, queryStartIndex);
  const hashStartIndex = url.indexOf('#', queryStartIndex);
  const queryString =
    hashStartIndex === -1
      ? url.slice(queryStartIndex + 1)
      : url.slice(queryStartIndex + 1, hashStartIndex);
  const hashString = hashStartIndex === -1 ? '' : url.slice(hashStartIndex);
  const nextQueryString = queryString
    .split('&')
    .filter((pair) => {
      const [key, value = ''] = pair.split('=');

      return !SELECTED_HOTEL_QUERY_KEYS.has(key) || value.length > 0;
    })
    .join('&');

  return nextQueryString
    ? `${routePath}?${nextQueryString}${hashString}`
    : `${routePath}${hashString}`;
};

export type SafetyNetSelectedHotelSource =
  | QueryClient
  | Pick<HotelFilters, 'safetyNetSelectedHotel'>
  | SafetyNetSelectedHotel
  | null
  | undefined;

const isQueryClient = (
  source: SafetyNetSelectedHotelSource,
): source is QueryClient =>
  Boolean(
    source &&
      typeof source === 'object' &&
      'getQueryData' in source &&
      typeof source.getQueryData === 'function',
  );

export const getSafetyNetSelectedHotel = (
  source?: SafetyNetSelectedHotelSource,
): SafetyNetSelectedHotel | null => {
  if (!source) {
    return null;
  }

  if (isQueryClient(source)) {
    return (
      source.getQueryData<HotelFilters>(FILTER_STATE_KEY)
        ?.safetyNetSelectedHotel ?? null
    );
  }

  if ('locationId' in source && 'hotelId' in source) {
    return source;
  }

  return source.safetyNetSelectedHotel ?? null;
};

export const recordSafetyNetSelectedHotel = (
  queryClient: QueryClient,
  locationId?: string,
  hotelId?: string,
) => {
  const normalizedLocationId = locationId?.trim() ?? '';
  const normalizedHotelId = hotelId?.trim() ?? '';
  if (!normalizedLocationId || !normalizedHotelId) {
    return;
  }

  queryClient.setQueryData<HotelFilters>(FILTER_STATE_KEY, (old) => ({
    ...(old ?? {
      ...getDefaultHotelFilters(),
      selectedHotelId: normalizedHotelId,
      selectedHotelName: '',
    }),
    safetyNetSelectedHotel: {
      locationId: normalizedLocationId,
      hotelId: normalizedHotelId,
    },
  }));
};

export const clearSafetyNetSelectedHotel = (queryClient: QueryClient) => {
  queryClient.setQueryData<HotelFilters>(FILTER_STATE_KEY, (old) => {
    if (!old || !('safetyNetSelectedHotel' in old)) {
      return old;
    }

    const { safetyNetSelectedHotel: _removed, ...rest } = old;
    return rest;
  });
};

export const isStaleSafetyNetSelectedHotelForLocation = (
  locationId?: string,
  hotelId?: string,
  source?: SafetyNetSelectedHotelSource,
) => {
  const normalizedLocationId = locationId?.trim() ?? '';
  const normalizedHotelId = hotelId?.trim() ?? '';
  const safetyNetSelectedHotel = getSafetyNetSelectedHotel(source);
  if (!normalizedLocationId || !normalizedHotelId || !safetyNetSelectedHotel) {
    return false;
  }

  return (
    safetyNetSelectedHotel.hotelId === normalizedHotelId &&
    Boolean(safetyNetSelectedHotel.locationId) &&
    safetyNetSelectedHotel.locationId !== normalizedLocationId
  );
};

export const syncSelectedHotelFilter = (
  queryClient: QueryClient,
  selectedHotelId: string,
  selectedHotelName: string,
) => {
  queryClient.setQueryData<HotelFilters>(FILTER_STATE_KEY, (old) => {
    const { safetyNetSelectedHotel: _removed, ...baseFilters } = (old ??
      getDefaultHotelFilters()) as Partial<HotelFilters> &
      Omit<HotelFilters, 'selectedHotelId' | 'selectedHotelName'>;

    return {
      ...baseFilters,
      selectedHotelId,
      selectedHotelName,
    };
  });
};
